import React, { useState, useEffect } from 'react';
import { Tag, Tooltip } from 'antd';
import { ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import moment from 'moment';
import { commonHelper } from '@iso/lib/helpers/commonHelper';

/**
 * Deadline Countdown Component
 * Displays a real-time countdown to a deadline with color coding based on urgency
 */
const DeadlineCountdown = ({ deadline, compact = false }) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    if (!deadline) {
      setTimeRemaining(null);
      return;
    }

    const calculateTimeRemaining = () => {
      const now = moment();
      // Convert UTC deadline to local time for accurate countdown
      const deadlineMoment = moment.utc(deadline).local();
      const duration = moment.duration(deadlineMoment.diff(now));

      if (duration.asMilliseconds() < 0) {
        setIsOverdue(true);
        setTimeRemaining({
          days: Math.abs(Math.floor(duration.asDays())),
          hours: Math.abs(duration.hours()),
          minutes: Math.abs(duration.minutes()),
          totalHours: Math.abs(Math.floor(duration.asHours()))
        });
      } else {
        setIsOverdue(false);
        setTimeRemaining({
          days: Math.floor(duration.asDays()),
          hours: duration.hours(),
          minutes: duration.minutes(),
          totalHours: Math.floor(duration.asHours())
        });
      }
    };

    // Initial calculation
    calculateTimeRemaining();

    // Update every minute
    const interval = setInterval(calculateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [deadline]);

  if (!deadline || !timeRemaining) {
    return null;
  }

  // Determine color based on time remaining
  const getColor = () => {
    if (isOverdue) return 'red';
    if (timeRemaining.totalHours < 4) return 'red';
    if (timeRemaining.totalHours < 24) return 'orange';
    if (timeRemaining.days < 2) return 'gold';
    return 'blue';
  };

  const getIcon = () => {
    return isOverdue ? <WarningOutlined /> : <ClockCircleOutlined />;
  };

  const getTimeText = () => {
    const { days, hours, minutes } = timeRemaining;

    if (compact) {
      if (isOverdue) {
        if (days > 0) return `${days}d overdue`;
        if (hours > 0) return `${hours}h overdue`;
        return `${minutes}m overdue`;
      } else {
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
      }
    } else {
      let parts = [];
      if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
      if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
      if (minutes > 0 && days === 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

      const timeString = parts.length > 0 ? parts.join(', ') : '< 1 minute';
      return isOverdue ? `${timeString} overdue` : timeString;
    }
  };

  const tooltipText = isOverdue
    ? `Overdue since ${commonHelper.displayDateTimeLocal(deadline)}`
    : `Due: ${commonHelper.displayDateTimeLocal(deadline)}`;

  return (
    <Tooltip title={tooltipText}>
      <Tag
        color={getColor()}
        icon={getIcon()}
        style={{
          fontSize: compact ? '12px' : '14px',
          fontWeight: isOverdue ? 'bold' : 'normal'
        }}
      >
        {getTimeText()}
      </Tag>
    </Tooltip>
  );
};

export default DeadlineCountdown;
